import { distance, length, lineSlice, nearestPointOnLine } from '@turf/turf';

const ARRIVE_M = 30;
const STEP_ADVANCE_M = 25;
const OFF_ROUTE_M = 80;
const CRUISE_KMH = 20;

export function maneuverIcon(type) {
  const map = {
    'new name': '⬆️', continue: '⬆️', straight: '⬆️',
    turn: '↩️', 'turn left': '⬅️', 'turn right': '➡️',
    'uturn': '🔃', 'sharp left': '⬅️', 'sharp right': '➡️',
    'slight left': '↖️', 'slight right': '↗️',
    'merge': '↗️', 'fork': '🔱', 'end of road': '🏁',
    'roundabout': '🔃', 'rotary': '🔃', 'exit roundabout': '➡️',
    'depart': '🚀', 'arrive': '🏁',
  };
  return map[type] || '⬆️';
}

export function maneuverText(maneuver) {
  if (!maneuver) return 'Continue';
  const type = maneuver.type;
  const mod = maneuver.modifier;
  if (type === 'arrive') return 'Arrive';
  if (type === 'depart') return 'Head out';
  if (type === 'roundabout' || type === 'rotary') return 'Enter roundabout';
  if (type === 'exit roundabout') return 'Exit roundabout';
  if (mod) return `${mod.charAt(0).toUpperCase() + mod.slice(1)}`;
  return 'Continue';
}

export class NavigationController {
  constructor(onUpdate, onArrive, onOffRoute) {
    this.onUpdate = onUpdate;
    this.onArrive = onArrive;
    this.onOffRoute = onOffRoute;
    this.route = null;
    this.steps = [];
    this.active = false;
    this.currentStep = 0;
    this.totalDist = 0;
    this.startTime = null;
    this.lastPos = null;
    this.offRouteCount = 0;
  }

  start(route) {
    if (!route?.geometry?.coordinates?.length) return false;
    this.route = route;
    this.steps = route.steps || [];
    this.active = true;
    this.currentStep = 0;
    this.totalDist = 0;
    this.startTime = Date.now();
    this.lastPos = null;
    this.offRouteCount = 0;
    this.emit();
    return true;
  }

  stop() {
    this.active = false;
    this.route = null;
    this.steps = [];
    this.currentStep = 0;
  }

  update(position) {
    if (!this.active || !this.route || !position) return;
    const { lat, lng } = position;
    const pt = [lng, lat];

    if (this.lastPos) {
      const d = distance([this.lastPos.lng, this.lastPos.lat], pt, { units: 'meters' });
      if (d > 0.5 && d < 200) this.totalDist += d;
    }
    this.lastPos = { lat, lng };

    const coords = this.route.geometry.coordinates;
    const snapped = nearestPointOnLine({ type: 'LineString', coordinates: coords }, { type: 'Point', coordinates: pt }, { units: 'meters' });
    const offRoute = snapped.properties.dist > OFF_ROUTE_M;
    if (offRoute) {
      this.offRouteCount++;
      if (this.offRouteCount > 3) {
        this.onOffRoute?.(snapped.properties.dist);
        this.offRouteCount = 0;
      }
    } else {
      this.offRouteCount = 0;
    }

    const idx = Math.min(Math.max(0, snapped.properties.index), coords.length - 1);
    const remainingCoords = coords.slice(idx);
    const remainingM = remainingCoords.length > 1
      ? length({ type: 'LineString', coordinates: remainingCoords }, { units: 'meters' })
      : 0;

    if (remainingM <= ARRIVE_M) {
      this.onArrive?.();
      return;
    }

    this.advanceStep(pt);
    this.emit(position, remainingM);
  }

  advanceStep(pt) {
    if (!this.steps.length) return;
    let idx = this.currentStep;
    while (idx < this.steps.length - 1) {
      const cur = this.steps[idx].maneuver?.location;
      const nxt = this.steps[idx + 1].maneuver?.location;
      if (!cur || !nxt) break;
      const dCur = distance([cur[0], cur[1]], pt, { units: 'meters' });
      const dNxt = distance([nxt[0], nxt[1]], pt, { units: 'meters' });
      if (dNxt < dCur - STEP_ADVANCE_M) idx++;
      else break;
    }
    this.currentStep = idx;
  }

  emit(position, remainingM) {
    const nextIdx = Math.min(this.currentStep + 1, this.steps.length - 1);
    const nextStep = this.steps[nextIdx];
    const currentStep = this.steps[this.currentStep];
    const speedKmh = position?.speedKmh || 0;
    const effSpeed = Math.max(speedKmh, CRUISE_KMH);
    const etaSec = remainingM > 0 ? remainingM / (effSpeed / 3.6) : 0;
    const eta = new Date(Date.now() + etaSec * 1000);

    let distToTurn = null;
    if (nextStep?.maneuver?.location) {
      distToTurn = distance([nextStep.maneuver.location[0], nextStep.maneuver.location[1]], [position.lng, position.lat], { units: 'meters' });
    }

    const progress = this.route.distance > 0
      ? Math.min(100, Math.max(0, (1 - remainingM / this.route.distance) * 100))
      : 0;

    this.onUpdate?.({
      active: this.active,
      speedKmh,
      remainingM,
      remainingKm: remainingM / 1000,
      eta: formatTime(eta),
      ridingTime: formatDuration(Date.now() - this.startTime),
      progress,
      turnIcon: maneuverIcon(nextStep?.maneuver?.modifier || nextStep?.maneuver?.type),
      turnText: maneuverText(nextStep?.maneuver),
      turnStreet: nextStep?.name || '',
      distToTurn: distToTurn != null ? formatDistance(distToTurn) : '',
      nextTurnText: this.steps[nextIdx + 1] ? `${maneuverText(this.steps[nextIdx + 1].maneuver)} on ${this.steps[nextIdx + 1].name || 'road'}` : '',
    });
  }
}

function formatTime(date) {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatDuration(ms) {
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return `${h}h ${rm}m`;
}

function formatDistance(m) {
  if (m < 1000) return `${Math.round(m)}m`;
  return `${(m / 1000).toFixed(1)}km`;
}
